import React, { Suspense, lazy } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "./theme/theme";
import { Paper, CssBaseline, CircularProgress, Box } from "@mui/material";
import ButtonAppBar from "./components/layout/CustomNav";
import ReleaseNotesDialog from "./components/ui/ReleaseNotesDialog";
import { Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import AuthService from "./services/auth.service";
import { LicenseInfo } from "@mui/x-license";

// Lazy-loaded components
const LoginOpti = lazy(() => import("./pages/Login"));
const Home = lazy(() => import("./pages/Home"));
const NewOrder = lazy(() => import("./features/order/NewOrder"));
const AdminLayout = lazy(() => import("./features/admin/Layout/AdminLayout"));
const Courier = lazy(() => import("./features/courier/Courier"));
const Today = lazy(() => import("./features/courier/Today"));
const Tomorrow = lazy(() => import("./features/courier/Tomorrow"));
const ManagerPanel = lazy(
  () => import("./features/mainuser/ManagerPanel"),
);
const ProfileOpti = lazy(() => import("./pages/Profile"));
const Agent = lazy(() => import("./features/agent/Agent"));
const AgentLayout = lazy(() => import("./features/agent/AgentLayout"));
const Doctor = lazy(() => import("./features/doctor/Doctor"));
const AddPacient = lazy(() => import("./features/doctor/Add_pacient"));
const StorePacients = lazy(() => import("./features/doctor/StorePacients"));
const EditOrder = lazy(() => import("./features/order/EditOrder"));
const DoctorAdmin = lazy(() => import("./features/doctor/admin/DoctorAdmin"));
const DoctorOrderMap = lazy(() => import("./features/doctor/admin/DoctorOrderMap"));
const LPU = lazy(() => import("./features/doctor/admin/LPU"));
const PaymentPage = lazy(() => import("./pages/Public/PaymentPage"));
const MessagePage = lazy(() => import("./features/chat/MessagePage"));
const Logisticians = lazy(() => import("./features/admin/Pages/Logisticians"));
const Leads = lazy(() => import("./features/admin/Pages/Leads"));

LicenseInfo.setLicenseKey(
  "e0d9bb8070ce0054c9d9ecb6e82cb58fTz0wLEU9MzI0NzIxNDQwMDAwMDAsUz1wcmVtaXVtLExNPXBlcnBldHVhbCxLVj0y",
);

const OrderPdfRedirect = () => {
  const { id } = useParams();
  const API_URL = process.env.REACT_APP_API_URL;
  const pdfUrl = `${API_URL}orderpdf/${id}`;
  window.location.href = pdfUrl;

  return null;
};

const ColorModeContext = React.createContext({ toggleColorMode: () => {} });
const user = AuthService.getCurrentUser();
const RoleAccess = ({ roles = [] }) => {
  return !roles.length || roles.includes(user?.role) ? (
    <Outlet />
  ) : (
    <Navigate to="/login" replace />
  );
};

function MyApp(props) {
  const toggleColorMode = props.toggleColorMode;
  return (
    <Paper>
      <Suspense
        fallback={
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100vh">
            <CircularProgress />
          </Box>
        }>
        <Routes>
          <Route
            path="/"
            element={
              user?.role === 6 ? (
                <Navigate to="/agent" replace />
              ) : user?.role === 3 ? (
                <Navigate to="/courier" replace />
              ) : user?.role === 7 ? (
                <Navigate to="/doctor" replace />
              ) : user?.role === 8 ? (
                <Navigate to="/doctor_admin" replace />
              ) : [1, 2, 4, 5, 9].includes(user?.role) ? (
                <Navigate to="/home" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/login"
            element={<LoginOpti toggleColorMode={toggleColorMode} />}
          />
          <Route path="/payment/:id" element={<PaymentPage />} />
          <Route path="/orderpdf/:id" element={<OrderPdfRedirect />} />

          <Route element={<RoleAccess roles={[1, 2, 4, 5, 8, 9]} />}>
            <Route path="/home" element={<Home />} />
          </Route>

          <Route element={<RoleAccess roles={[1]} />}>
            <Route path="/logisticians" element={<Logisticians />} />
          </Route>
          <Route element={<RoleAccess roles={[1, 9]} />}>
            <Route path="/leads" element={<Leads />} />
          </Route>

          <Route element={<RoleAccess roles={[1, 2, 4, 5, 7, 8]} />}>
            <Route path="/neworder" element={<NewOrder id={0} />} />
            <Route path="/order/:id" element={<EditOrder />} />
          </Route>
          <Route element={<RoleAccess roles={[1]} />}>
            <Route path="/admin" element={<AdminLayout />} />
          </Route>
          <Route element={<RoleAccess roles={[1, 2]} />}>
            <Route path="/messages" element={<MessagePage />} />
          </Route>
          <Route element={<RoleAccess roles={[4]} />}>
            <Route path="/mod" element={<ManagerPanel />} />
          </Route>
          <Route element={<RoleAccess roles={[3]} />}>
            <Route path="/courier" element={<Courier />} />
            <Route path="/today" element={<Today />} />
            <Route path="/tomorrow" element={<Tomorrow />} />
          </Route>
          <Route element={<RoleAccess roles={[6]} />}>
            <Route path="/agent" element={<Agent />} />
            <Route path="/agentpanel" element={<AgentLayout />} />
          </Route>
          <Route element={<RoleAccess roles={[7]} />}>
            <Route path="/doctor" element={<Doctor />} />
            <Route path="/add_pacient" element={<AddPacient />} />
            <Route path="/store_pacients" element={<StorePacients />} />
          </Route>
          <Route element={<RoleAccess roles={[8]} />}>
            <Route path="/doctor_admin" element={<DoctorAdmin />} />
            <Route path="/order-map" element={<DoctorOrderMap />} />
          </Route>
          <Route element={<RoleAccess roles={[8, 4]} />}>
            <Route path="/lpu" element={<LPU />} />
          </Route>
          <Route element={<RoleAccess roles={[1, 2, 3, 4, 5, 6, 7, 8, 9]} />}>
            <Route path="/profile" element={<ProfileOpti />} />
          </Route>
        </Routes>
      </Suspense>
    </Paper>
  );
}

export default function ToggleColorMode() {
  const user = AuthService.getCurrentUser();
  const [mode, setMode] = React.useState(
    localStorage.getItem("theme") || "light",
  );
  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode(prevMode => (prevMode === "light" ? "dark" : "light"));
        localStorage.setItem("theme", mode === "dark" ? "light" : "dark");
      },
    }),
    [mode],
  );

  const theme = React.useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {user && <ButtonAppBar toggleColorMode={colorMode.toggleColorMode} />}
        {user && <ReleaseNotesDialog />}
        <MyApp toggleColorMode={colorMode.toggleColorMode} />
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
